/**
 * SIEVE — Ingest Triage (LLM verdicts on shared content). Pure model: types + deterministic helpers
 * shared by the server gate (`server-api/src/ingestTriage/`) and the client surfaces. No I/O here.
 *
 * See `docs/plans/ingest-triage.md` (spec, decisions S1–S12) and
 * `docs/plans/ingest-triage-decision-log.md` (implementation decisions IMPL-S*).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** S2 — three verdicts (the article's four collapse: Read≡ingest, Think→ingest-flag, Drop→park). */
export type TriageVerdict = 'ingest' | 'ingest-flag' | 'park';
export type TriageConfidence = 'high' | 'medium' | 'low';
/** S10 — per-source rollout mode. `shadow` judges + logs but never parks. */
export type TriageMode = 'off' | 'shadow' | 'enforce';
/** S1 — v1 scope: the two EXTERNAL shared-content paths. Never user-authored or system-composed notes. */
export type TriageSource = 'instagram' | 'email-inbox';

/** Per-source modes, stored on `user.preferences.ingestTriage` (S10). */
export interface IngestTriageConfig {
  instagram: TriageMode;
  emailInbox: TriageMode;
}

export const DEFAULT_INGEST_TRIAGE_CONFIG: IngestTriageConfig = { instagram: 'off', emailInbox: 'off' };

const MODES: readonly TriageMode[] = ['off', 'shadow', 'enforce'];

/** Tolerant read of the preferences blob — anything malformed degrades to `off` (fail-open posture). */
export function coerceIngestTriageConfig(v: unknown): IngestTriageConfig {
  const o = (v ?? {}) as Record<string, unknown>;
  const mode = (x: unknown): TriageMode => (MODES.includes(x as TriageMode) ? (x as TriageMode) : 'off');
  return { instagram: mode(o.instagram), emailInbox: mode(o.emailInbox) };
}

export interface TriageDecision {
  verdict: TriageVerdict;
  /** One line, user-visible on the filtered card / parked list (S8). */
  reason: string;
  confidence: TriageConfidence;
  /** S4 audit — the model failed (error/timeout/garbage) and the gate defaulted to `ingest`. */
  failedOpen?: boolean;
}

/**
 * S5 — the confidence gate, pinned as a pure function: `park` requires HIGH confidence; anything less
 * keeps the content (bias to keep — a missed filter is free, lost content is not).
 */
export function applyConfidenceGate(d: TriageDecision): TriageDecision {
  if (d.verdict === 'park' && d.confidence !== 'high') {
    return { ...d, verdict: 'ingest', reason: `${d.reason} (kept: confidence ${d.confidence})` };
  }
  return d;
}

/** S6 — parked payload retention before GC. */
export const PARKED_TTL_MS = 90 * DAY_MS;

export type ParkedStatus = 'parked' | 'restored' | 'expired';

/** S6 — a filtered item: a POINTER to the retained source record, never a copy. Nothing is deleted. */
export interface ParkedIngest {
  parkedId: string;
  userId: string;
  dateKey: string;
  source: TriageSource;
  /** The retained record to re-run on restore: GRAM importId, or the email record id. */
  sourceId: string;
  title: string;
  reason: string;
  status: ParkedStatus;
  /** The recap row this park logged (S7) — patched `undone` on restore. */
  autonomyEventId?: string;
  createdAt: number;
  expiresAt: number;
}

/** S11 — one verdict event; the stream TUNE consumes. Append-only except `outcome`. */
export interface IngestTriageLogEntry {
  logId: string;
  userId: string;
  dateKey: string;
  source: TriageSource;
  mode: TriageMode;
  sourceId: string;
  title: string;
  decision: TriageDecision;
  /** Set when a parked item was restored — the precision-miss marker (S7/S11). */
  outcome?: 'restored';
  createdAt: number;
}

/** S11 — precision proxy over enforced parks: 1 − restores/parks (1 when no parks yet). */
export function triagePrecision(entries: Array<Pick<IngestTriageLogEntry, 'mode' | 'decision' | 'outcome'>>): {
  parks: number;
  restores: number;
  precision: number;
} {
  const parks = entries.filter((e) => e.mode === 'enforce' && e.decision.verdict === 'park');
  const restores = parks.filter((e) => e.outcome === 'restored');
  return {
    parks: parks.length,
    restores: restores.length,
    precision: parks.length === 0 ? 1 : 1 - restores.length / parks.length,
  };
}

/** S7 — the AXIS undo-token namespace for a park (recap undo → restore). */
export function sieveUndoToken(parkedId: string): string {
  return `sieve:${parkedId}`;
}
export function parseSieveUndoToken(token: string): string | null {
  return token.startsWith('sieve:') ? token.slice('sieve:'.length) : null;
}
